
import { defineComponent, PropType, ref, computed } from "vue";
import { ResultStatus, OrderType, AlertType } from "@/stores/telegramAlerts";

export interface AlertEditData {
  stake?: string | number;
  odds?: number | string;
  resultStatus?: ResultStatus;
  pair?: string;
  direction?: string;
  orderType?: OrderType;
  entryPrice?: number;
  stopLoss?: number;
  takeProfit?: number[];
  exitPrice?: number;
  index?: string;
}

export default defineComponent({
  name: "AlertEditForm",
  props: {
    initialData: {
      type: Object as PropType<AlertEditData>,
      required: true,
    },
    alertType: {
      type: String,
      default: "sports_betting",
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["save", "cancel"],
  setup(props, { emit }) {
    // Transform initialData to local form
    // We map 'pair' OR 'index' to a generic 'target' field for the input,
    // then split it back on save if needed or just send the right one.

    const form = ref({
      stake: props.initialData.stake,
      odds: props.initialData.odds,
      resultStatus: props.initialData.resultStatus || ResultStatus.PENDING,
      target: props.initialData.pair || props.initialData.index || "",
      direction: props.initialData.direction,
      orderType: props.initialData.orderType || OrderType.MARKET,
      entryPrice: props.initialData.entryPrice,
      stopLoss: props.initialData.stopLoss,
      takeProfit: props.initialData.takeProfit || [],
      exitPrice: props.initialData.exitPrice,
    });

    const takeProfitStr = ref(form.value.takeProfit.join(", "));

    const updateTakeProfit = () => {
      form.value.takeProfit = takeProfitStr.value
        .split(",")
        .map((s) => Number(s.trim()))
        .filter((n) => !isNaN(n));
    };

    // Validation state
    const stakeError = ref("");
    const oddsError = ref("");

    // Validation functions
    const validateStake = (stake: string | number | undefined): string => {
      if (!stake) return ""; // Optional field

      const stakeStr = String(stake).trim();

      // Check for negative values
      if (stakeStr.startsWith("-")) {
        return "El stake no puede ser negativo";
      }

      // Check if it's a valid format
      const withoutPercent = stakeStr.endsWith("%")
        ? stakeStr.slice(0, -1).trim()
        : stakeStr;

      if (!/^\d+(\.\d+)?$/.test(withoutPercent)) {
        return "El stake debe ser un número válido (ej: 10, 0.1, 10%)";
      }

      const parsed = parseFloat(withoutPercent);
      if (isNaN(parsed) || parsed < 0) {
        return "El stake debe ser un número positivo";
      }

      return "";
    };

    const validateOdds = (odds: string | number | undefined): string => {
      if (!odds) return ""; // Optional field

      const oddsStr = String(odds).trim();

      // Check for negative decimal odds (but not American odds like -110)
      if (/^-\d+(\.\d+)$/.test(oddsStr)) {
        return "El momio decimal no puede ser negativo";
      }

      // Try to detect format
      // American format: +150, -110
      if (/^[+-]\d+$/.test(oddsStr)) {
        const value = parseInt(oddsStr, 10);
        if (value === 0) {
          return "El momio americano no puede ser 0";
        }
        return "";
      }

      // Fractional format: 6/4
      if (/^\d+\/\d+$/.test(oddsStr)) {
        const parts = oddsStr.split("/");
        const denominator = parseInt(parts[1], 10);
        if (denominator === 0) {
          return "El denominador del momio fraccional no puede ser 0";
        }
        return "";
      }

      // Decimal format: 1.85
      if (!/^\d+(\.\d+)?$/.test(oddsStr)) {
        return "El momio debe ser un número válido (ej: 1.85, +150, 6/4)";
      }

      const parsed = parseFloat(oddsStr);
      if (isNaN(parsed) || !isFinite(parsed) || parsed < 1) {
        return "El momio decimal debe ser mayor o igual a 1.0";
      }

      return "";
    };

    const validateFields = () => {
      if (props.alertType === AlertType.SPORTS_BETTING) {
        stakeError.value = validateStake(form.value.stake);
        oddsError.value = validateOdds(form.value.odds);
      }
    };

    const validationErrors = computed(() => {
      const errors: string[] = [];
      if (stakeError.value) {
        errors.push(`Stake: ${stakeError.value}`);
      }
      if (oddsError.value) {
        errors.push(`Cuota: ${oddsError.value}`);
      }
      return errors;
    });

    const hasValidationErrors = computed(() => {
      return stakeError.value !== "" || oddsError.value !== "";
    });

    const save = () => {
      // Validate before saving
      validateFields();

      if (hasValidationErrors.value) {
        return; // Don't proceed if there are validation errors
      }

      // Reconstruct payload - only include fields relevant to the alert type
      const payload: AlertEditData = {
        resultStatus: form.value.resultStatus,
      };

      // Include alert-type specific fields
      if (props.alertType === AlertType.SPORTS_BETTING) {
        // Sports betting specific fields
        payload.stake = form.value.stake;
        payload.odds = form.value.odds;
      } else if (props.alertType === AlertType.FOREX) {
        // Forex specific fields
        payload.pair = form.value.target;
        payload.direction = form.value.direction;
        payload.orderType = form.value.orderType;
        payload.entryPrice = form.value.entryPrice;
        payload.stopLoss = form.value.stopLoss;
        payload.takeProfit = form.value.takeProfit;
        payload.exitPrice = form.value.exitPrice;
      } else if (props.alertType === AlertType.SYNTHETIC_INDICES) {
        // Synthetic indices specific fields
        payload.index = form.value.target;
        payload.direction = form.value.direction;
        payload.orderType = form.value.orderType;
        payload.entryPrice = form.value.entryPrice;
        payload.stopLoss = form.value.stopLoss;
        payload.takeProfit = form.value.takeProfit;
        payload.exitPrice = form.value.exitPrice;
      }

      emit("save", payload);
    };

    return {
      AlertType,
      form,
      takeProfitStr,
      updateTakeProfit,
      save,
      stakeError,
      oddsError,
      validateFields,
      validationErrors,
      hasValidationErrors,
    };
  },
});
